import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  TextField,
  Button,
  Divider,
  AppBar,
  Toolbar,
  IconButton as MuiIconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { CartItem } from '../App';

interface MobileCartPageProps {
  cart: CartItem[];
  onBack: () => void;
  onUpdateQty: (deviceId: number, qty: number) => void;
  onRemoveItem: (deviceId: number) => void;
  onCheckout: () => void;
  totalMinor: number;
  currency: string;
  notes: string;
  onNotesChange: (notes: string) => void;
}

function currency(amount: number, currency: string) {
  const value = Number(amount || 0);
  return `${currency} ${value.toFixed(2)}`;
}

export default function MobileCartPage({
  cart,
  onBack,
  onUpdateQty,
  onRemoveItem,
  onCheckout,
  totalMinor,
  currency: cartCurrency,
  notes,
  onNotesChange
}: MobileCartPageProps) {
  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default'
      }}
    >
      {/* Mobile Header */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ minHeight: 48, px: 2 }}>
          <MuiIconButton
            onClick={onBack}
            sx={{
              color: '#e6ac30',
              mr: 1,
              '&:hover': {
                bgcolor: 'rgba(230, 172, 48, 0.1)',
              }
            }}
            aria-label="Back to products"
          >
            <ArrowBackIcon />
          </MuiIconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 900,
              color: '#e6ac30',
              fontSize: '1.1rem'
            }}
          >
            Cart ({cart.reduce((sum, item) => sum + item.quantity, 0)} items)
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Cart Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 1, // Reduced top padding
          pb: 14, // Extra padding to ensure content is visible above fixed checkout button
          // Hide scrollbar but keep functionality
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Stack spacing={2}>
          {cart.length === 0 ? (
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 2,
                bgcolor: 'grey.50'
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Your cart is empty
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add some products to get started
              </Typography>
            </Paper>
          ) : (
            <>
              {/* Cart Items */}
              <Stack spacing={1.5}>
                {cart.map((item) => (
                  <Paper
                    key={item.device_id}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {currency(item.unit_price_minor, item.currency)} each
                        </Typography>
                      </Box>

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => onUpdateQty(item.device_id, Math.max(item.quantity - 1, 0))}
                          sx={{
                            bgcolor: 'grey.100',
                            '&:hover': { bgcolor: 'grey.200' }
                          }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>

                        <TextField
                          size="small"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => onUpdateQty(item.device_id, Math.max(Number(e.target.value), 0))}
                          sx={{
                            width: 80,
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'grey.50'
                            }
                          }}
                          inputProps={{
                            min: 0,
                            style: { textAlign: 'center' }
                          }}
                        />

                        <IconButton
                          size="small"
                          onClick={() => onUpdateQty(item.device_id, item.quantity + 1)}
                          sx={{
                            bgcolor: 'grey.100',
                            '&:hover': { bgcolor: 'grey.200' }
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>

                        <Box sx={{ flex: 1 }} />

                        <Button
                          size="small"
                          variant="text"
                          color="error"
                          onClick={() => onRemoveItem(item.device_id)}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          Remove
                        </Button>
                      </Stack>

                      <Divider />

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Subtotal
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          {currency(item.unit_price_minor * item.quantity, item.currency)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>

              {/* Order Notes */}
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Order Notes
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    If you'd like to order a quantity different from what is listed, or have special requests, please add them here.
                  </Typography>
                  <TextField
                    label="Notes (optional)"
                    value={notes}
                    onChange={(e) => onNotesChange(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Stack>
              </Paper>

              {/* Order Summary */}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Total
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: '#e6ac30' }}>
                      {currency(totalMinor, cartCurrency)}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </>
          )}
        </Stack>
      </Box>

      {/* Sticky Checkout Button */}
      {cart.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'white',
            borderTop: '1px solid',
            borderColor: 'divider',
            p: 2,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
            // Add padding at bottom to account for safe area on mobile devices
            pb: { xs: 3, sm: 2 },
          }}
        >
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={onCheckout}
            sx={{
              bgcolor: '#e6ac30',
              '&:hover': {
                bgcolor: '#d4a52a',
              },
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 700,
              borderRadius: 2,
            }}
          >
            Proceed to Checkout
          </Button>
        </Box>
      )}
    </Box>
  );
}